import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { TemplateLayoutService } from '@fi-app/template/template-layout.service';
import { HtmlPreviewService } from '@fi-shared/fi-print-download/html-preview.service';
import { FormValidatorService } from '@fi-app/utilities/fi-services/form-validator.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { cloneDeep } from '@fi-shared/utils/clone-deep';
import { StatisticService } from '@fi-app/shared/statistic/statistic.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { ObjectTypes } from '@fi-shared/types/object-type';
import {
    FiDocumentTemplateBasicStructures,
    IDocumentTemplateBasicStructure,
} from '@fi-app/document-template/document-template-basic-structures';
import { DocumentTemplateService } from '@fi-app/document-template/document-template.service';
import { Subject } from 'rxjs';
import { FiDocumentTemplateEditGeneralComponent } from '@fi-app/document-template/document-template-edit-general/document-template-edit-general.component';
import { first } from 'rxjs/operators';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { TemplateModel } from '@fi-app/template/template.model';
import { TemplateLayoutModel } from '@fi-app/template/template-layout.model';
import { IDocumentTemplateEditPageSections } from '@fi-app/document-template/document-template';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';

enum FiDocumentTemplateEditPageSections {
    Basic = 'basic',
    Layout = 'layout',
    Settings = 'settings',
}

@Component({
    selector: 'fi-document-template-edit',
    templateUrl: './document-template-edit.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./document-template-edit.component.scss'],
    host: { class: 'fi-document-template-edit' },
    standalone: false,
})
export class FiDocumentTemplateEditComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input('template') Template: TemplateModel;

    @ViewChild(FiDocumentTemplateEditGeneralComponent, { static: false })
    documentTemplateEditGeneral: FiDocumentTemplateEditGeneralComponent;

    PageSections: IDocumentTemplateEditPageSections[];
    Title: string;
    MoreMenuItems = new MoreMenuMobileSettingsModel();
    IsTriggeredSaving: boolean = false;
    SelectedStructure: IDocumentTemplateBasicStructure = null;
    ScreenHTML = '';
    ShowPage: string;
    templateLayout: any;
    ObjectTypes = ObjectTypes;
    BasicStructures = FiDocumentTemplateBasicStructures;
    SelectSectionCheck: (section) => boolean | void;
    headerText: string;
    EditTabs: EditTabsModel;

    private ccmTemplateManagerClassName = 'ccm-template-manager';
    private templateEditPageContentIdName = 'TemplateEditPageContent';

    constructor(
        private appTextService: AppTextService,
        private templateLayoutService: TemplateLayoutService,
        private htmlPreviewService: HtmlPreviewService,
        private formValidatorService: FormValidatorService,
        private loadingService: LoadingService,
        private saveChangesWarningService: SaveChangesWarningService,
        private stateService: FiStateService,
        private statisticService: StatisticService,
        private templatesService: DocumentTemplateService,
        private objectTitleService: ObjectTitleService,
        private textEditorHistoryService: TextEditorHistoryService,
    ) {}

    ngOnInit(): void {
        this.textEditorHistoryService.resetChangesHistoryData();
        this.headerText = this.Template.Id
            ? this.objectTitleService.getEditTitle(this.Template.Name)
            : this.objectTitleService.getCreateTitle(ObjectTypes.Template);

        this.PageSections = [
            {
                key: FiDocumentTemplateEditPageSections.Basic,
                title: this.appTextService.get('template.select_basic'),
            },
            {
                key: FiDocumentTemplateEditPageSections.Layout,
                title: this.appTextService.get('common.content'),
                disabled: true,
            },
            {
                key: FiDocumentTemplateEditPageSections.Settings,
                title: this.appTextService.get('template.general_settings'),
                disabled: true,
            },
        ];

        this.ShowPage = this.Template.Id
            ? FiDocumentTemplateEditPageSections.Layout
            : FiDocumentTemplateEditPageSections.Basic;

        this.MoreMenuItems.AddListItem(this.appTextService.get('common.save_and_close'), () => this.Save());

        if (this.Template.Id) {
            const newLayout: TemplateLayoutModel = this.templateLayoutService.GetLayout(this.Template);
            this.setTemplateLayout(newLayout);
            this.templateLayoutService.TemplatePlaceHolderCollectionToLayout(
                this.Template.PlaceHolderCollection,
                newLayout,
            );
            this.BasicStructures.forEach((Structure) => {
                if (Structure.Id == this.Template.BaseUrl) this.SelectedStructure = Structure;
            });
        }

        if (this.SelectedStructure) this.enableAllSections();

        this.statisticService.Visit(ModuleType.Template, ObjectTypes.Template, this.stateService.params.Id);

        this.SelectSectionCheck = (section: string) => {
            if (this.IsTriggeredSaving)
                this.formValidatorService.validateForm(document.getElementById(this.templateEditPageContentIdName));

            if (this.SelectedStructure && this.Template.BaseUrl != this.SelectedStructure.Id) {
                this.Template.BaseUrl = this.SelectedStructure.Id;
                this.setTemplateLayout(this.templateLayoutService.GetLayout(this.Template));
                this.ShowPage = section;
                return false;
            }

            if (!this.templateLayout) return false;

            if (this.isLayoutPageSection() && section != this.ShowPage) this.SaveScreenHTML();
        };

        this.initTabsModel();
    }

    private initTabsModel(): void {
        this.EditTabs = new EditTabsModel(this.headerText);
        this.EditTabs.ButtonBackTitle = this.appTextService.get('back');
    }

    isBasicPageSection(): boolean {
        return this.ShowPage === FiDocumentTemplateEditPageSections.Basic;
    }

    isLayoutPageSection(): boolean {
        return this.ShowPage === FiDocumentTemplateEditPageSections.Layout;
    }

    isSettingsPageSection(): boolean {
        return this.ShowPage === FiDocumentTemplateEditPageSections.Settings;
    }

    ngAfterViewInit(): void {
        this.documentTemplateEditGeneral.accessRightFilters.onInitialized$.pipe(first()).subscribe(() => {
            this.saveChangesWarningService.start(() => this.getModelData());
        });
    }

    private getModelData() {
        const template: TemplateModel = cloneDeep(this.Template);
        return {
            template: template,
            templateLayout: this.templateLayout,
        };
    }

    // note: not used ?
    SelectSection() {
        return this.SelectSectionCheck;
    }

    TemplateSectionNext() {
        if (this.isLayoutPageSection()) {
            this.SaveScreenHTML();
        }

        const map = [];
        this.PageSections.forEach((pageTemplate: IDocumentTemplateEditPageSections) => {
            map.push(pageTemplate.key);
        });
        this.ShowPage = this.PageSections[map.indexOf(this.ShowPage) + 1].key;
    }

    private setTemplateLayout(newLayout) {
        if (this.templateLayout) {
            this.Template.PlaceHolderCollection = this.templateLayoutService.LayoutToTemplatePlaceHolderCollection(
                this.templateLayout,
                this.Template,
            );
            this.templateLayoutService.TemplatePlaceHolderCollectionToLayout(
                this.Template.PlaceHolderCollection,
                newLayout,
            );
        }
        this.templateLayout = newLayout;
    }

    SelectBasicStructureForCreateTemplate(SelectedStructure) {
        this.Template.BaseUrl = SelectedStructure.Id;
        this.setTemplateLayout(this.templateLayoutService.GetLayout(this.Template));
        this.TemplateSectionNext();
    }

    SaveScreenHTML() {
        this.ScreenHTML = this.htmlPreviewService.getPreviewHtml(
            document.getElementsByClassName(this.ccmTemplateManagerClassName)[0],
        );
    }

    Save() {
        this.IsTriggeredSaving = true;
        if (!this.formValidatorService.validateForm(document.getElementById(this.templateEditPageContentIdName))) {
            this.formValidatorService.showErrorMessage();
            return false;
        }

        if (this.ShowPage === FiDocumentTemplateEditPageSections.Layout) this.SaveScreenHTML();

        this.Template.PlaceHolderCollection = this.templateLayoutService.LayoutToTemplatePlaceHolderCollection(
            this.templateLayout,
            this.Template,
        );
        this.loadingService.trackPromise(this.templatesService.SaveTemplate(this.Template)).then((response: number) => {
            this.Template.Id = response;
            this.documentTemplateEditGeneral.accessRightFilters.OnAccessRightFilterCollectionSaved();
            this.loadingService
                .trackPromise(this.templatesService.GenerateTemplatePreviewFileImage(this.Template.Id, this.ScreenHTML))
                .then(() => {
                    this.saveChangesWarningService.stop();
                    this.textEditorHistoryService.resetChangesHistoryData();
                    this.stateService.go(FiStateNames.Document.Template.List);
                });
        });
    }

    selectedStructureChange($event: IDocumentTemplateBasicStructure) {
        this.SelectedStructure = $event;
        this.enableAllSections();
    }

    enableAllSections() {
        this.PageSections.forEach((section: IDocumentTemplateEditPageSections) => (section.disabled = false));
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
